// src/data/attestationLink.js
//
// RULING 330 PART 3 - OFFICER ATTESTATION BY ONE-TIME LINK. Task list section 786.
//
// PURE. The routed Local Chairperson and the Local's Secretary-Treasurer get a contentless notice carrying a link; the
// link opens a page with the applicant's name, roster number, seniority date and Local and three acts. No account, no
// password. This module plans the link, judges a redemption and plans the act; the callable mints the token (crypto),
// stores only its hash, and writes; firestore.rules lets no client write links or attestations (ruling 330.1).
//
// THE RULES, from ruling 330 (Bob 2026-10-23 11:06 ET):
//   * 7 days, single use, bound to the application and the officer's e-mail of record.
//   * opened twice, expired, or from an address not on record: refused and logged; the officer is told to ask the
//     General Chairperson for a new one.
//   * every act logged: who, when, which link.
//   * no member name, number or date in any e-mail (section 776) - the link carries an opaque token and nothing else.
//   * the applicant never sees who attested or what they said, beyond his own application's status.
//   * attestation informs; the Local Chairperson decides (ruling 193).

const F = Object.freeze;

export const LINK_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
export const ATTEST_OFFICES = F({ lc: 'Local Chairperson', st: 'Secretary-Treasurer' });

export const ATTEST_ACTS = F({
  confirm: F({ words: 'Confirm', finding: 'confirmed he is on the roster and in good standing' }),
  not_on_roster: F({ words: 'Not on my roster', finding: 'said he is not on the roster' }),
  not_in_good_standing: F({ words: 'Not in good standing', finding: 'said he is not in good standing' }),
});
export const ATTEST_ACT_KINDS = F(Object.keys(ATTEST_ACTS));

export const REFUSAL_WORDS = 'This link cannot be used. Ask your General Chairperson for a new one.';
export const REFUSAL_REASONS = F(['unknown', 'used', 'expired', 'wrong_application', 'address_not_on_record']);

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

// The link record as stored. `tokenHash` is the hash of the token the callable minted; the token itself is never stored.
export function planLink({ applicationId, officerEmail, office, tokenHash, nowMs }) {
  const out = [];
  if (!String(applicationId || '').trim()) out.push('applicationId is required');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(officerEmail || '').trim())) out.push('the officer e-mail of record is required');
  if (!Object.prototype.hasOwnProperty.call(ATTEST_OFFICES, office)) out.push('office must be lc or st');
  if (!/^[0-9a-f]{64}$/.test(String(tokenHash || ''))) out.push('tokenHash must be a sha-256 hex digest');
  if (!Number.isFinite(nowMs)) out.push('nowMs is required');
  if (out.length) return F({ ok: false, problems: F(out) });
  return F({ ok: true, link: F({ applicationId: String(applicationId), officerEmail: norm(officerEmail), office, tokenHash,
    issuedAtMs: nowMs, expiresAtMs: nowMs + LINK_DAYS * DAY_MS, usedAtMs: null }) });
}

// Judges a redemption. `link` is the stored record found by the token's hash (null when none matches); `who` is the
// e-mail the officer proves at the page (the address the notice went to, confirmed by a one-time code).
export function checkRedeem(link, { applicationId, email, nowMs }) {
  const refuse = (reason) => F({ ok: false, reason, words: REFUSAL_WORDS });
  if (!link) return refuse('unknown');
  if (link.usedAtMs != null) return refuse('used');
  if (!Number.isFinite(nowMs) || nowMs >= link.expiresAtMs) return refuse('expired');
  if (String(applicationId || '') !== link.applicationId) return refuse('wrong_application');
  if (norm(email) !== link.officerEmail) return refuse('address_not_on_record');
  return F({ ok: true, reason: '', words: '' });
}

// The act, planned. Returns the attestation record to write and the link's used stamp; the caller writes both in one
// transaction with the application's audit statement.
export function planAttest(link, kind, nowMs, rosterAttached) {
  if (!ATTEST_ACT_KINDS.includes(kind)) return F({ ok: false, problems: F(['act must be one of ' + ATTEST_ACT_KINDS.join(', ')]) });
  const office = ATTEST_OFFICES[link.office];
  return F({ ok: true,
    attestation: F({ applicationId: link.applicationId, office: link.office, officerEmail: link.officerEmail,
      act: kind, atMs: nowMs, linkHash: link.tokenHash, rosterAttached: !!rosterAttached }),
    linkUsedAtMs: nowMs,
    statement: 'The ' + office + ' ' + ATTEST_ACTS[kind].finding + ' by attestation link.' });
}

// The notice that carries the link: no applicant name, number, date or Local - only that an application waits.
export function attestNotice(office, url) {
  return F({ subject: 'An application is waiting for your confirmation',
    body: 'An application in your Local is waiting for your confirmation as ' + ATTEST_OFFICES[office]
      + '. Open this link to see it. It works once and for ' + LINK_DAYS + ' days: ' + url });
}

// What the applicant may see of the attestations: his application's state, never who said what.
export function applicantView(attestations) {
  const list = Array.isArray(attestations) ? attestations : [];
  return list.length ? 'Your local officers have been asked to confirm your application, and have answered.'
    : 'Your local officers have been asked to confirm your application.';
}

// What the Local Chairperson's and General Chairperson's desks show beside the match (ruling 207.1).
export function deskLines(attestations) {
  return (Array.isArray(attestations) ? attestations : []).slice().sort((a, b) => a.atMs - b.atMs)
    .map((a) => ATTEST_OFFICES[a.office] + ' (' + a.officerEmail + ') ' + ATTEST_ACTS[a.act].finding
      + (a.rosterAttached ? ', and said the roster will be sent to the General Chairperson' : '') + '.');
}
